import { StrictMode } from 'react';
import { hydrateRoot, createRoot } from 'react-dom/client';
import App from './App.tsx';
import { loadLocale, type Dict } from './utils/i18n';
import { langFromPath } from './utils/languages';
import './index.css';

// Apply saved theme before React renders to prevent flash
try {
  if (localStorage.getItem('npw_theme') === 'dark') {
    document.documentElement.classList.add('dark');
  }
} catch { /* localStorage blocked */ }

const rootEl = document.getElementById('root')!;

/**
 * Hydrate localized SSG pages with the same dictionary used on the server.
 * The HTML stays visible while this small JSON file loads, avoiding an English
 * first render and the resulting hydration mismatch on prefixed routes.
 */
async function mount(): Promise<void> {
  const pathLang = langFromPath(window.location.pathname);
  const initialLang = pathLang ?? undefined;
  let initialDict: Dict | undefined;
  let localeLoadFailed = false;
  if (pathLang) {
    try {
      initialDict = await loadLocale(pathLang);
    } catch {
      localeLoadFailed = true;
    }
  }
  const app = (
    <StrictMode>
      <App initialLang={initialLang} initialDict={initialDict} />
    </StrictMode>
  );

  // In production (SSG), the root contains rendered elements and is hydrated.
  // In dev, Vite leaves only the <!--app-html--> placeholder comment; checking
  // innerHTML would mistake that comment for real server markup.
  if (rootEl.childElementCount > 0 && !localeLoadFailed) {
    hydrateRoot(rootEl, app);
  } else {
    // Avoid hydrating localized SSG markup with the bundled English fallback.
    // LanguageProvider retries the locale request after this clean mount.
    if (rootEl.childElementCount > 0) rootEl.replaceChildren();
    createRoot(rootEl).render(app);
  }
}

void mount();

/**
 * Register the service worker.
 *
 * Deferred to `load` so it never competes with hydration for bandwidth — the
 * worker's only job is the *next* visit, so nothing is gained by fetching it
 * during the first paint.
 *
 * Production only. In dev a worker would cache Vite's module graph and serve
 * stale code back after an edit, which is a genuinely confusing failure.
 */
const isEmbedRoute = location.pathname === '/embed' || location.pathname.startsWith('/embed/');
if (import.meta.env.PROD && !isEmbedRoute && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {
      // Registration fails on insecure origins and where a policy blocks
      // workers. The site works identically without one, so this is not worth
      // reporting to the user.
    });
  });
}
